import { signal } from '@preact/signals';

/** Hash-based routing (#/path?query) so the built site works on any static host and file://. */
export interface Route {
  path: string;
  query: URLSearchParams;
}

function parseHash(): Route {
  const raw = location.hash.replace(/^#/, '') || '/';
  const qi = raw.indexOf('?');
  const pathPart = qi === -1 ? raw : raw.slice(0, qi);
  const queryPart = qi === -1 ? '' : raw.slice(qi + 1);
  return {
    path: pathPart.startsWith('/') ? pathPart : '/' + pathPart,
    query: new URLSearchParams(queryPart),
  };
}

export const route = signal<Route>(parseHash());

// Every history entry gets a stable id (kept in history.state, so it survives back/forward and reloads).
// Scroll positions are remembered per id: returning to an entry restores where the user was, while a
// fresh navigation (a link click, navigate()) starts at the top.
type NavState = { navId: number } | null;
const scrollPositions = new Map<number, number>();
// sessionStorage/history are absent under vitest (node environment); the router then just parses hashes.
const session: Pick<Storage, 'getItem' | 'setItem'> = typeof sessionStorage === 'undefined' ? { getItem: () => null, setItem: () => undefined } : sessionStorage;
const hist = typeof history === 'undefined' ? undefined : history;
let seq = Number(session.getItem('ct:nav:seq') ?? '0');
const firstId = Number(session.getItem('ct:nav:first') ?? '0') || undefined;
function ensureNavId(): number {
  const st = (hist?.state ?? null) as NavState;
  if (st && typeof st.navId === 'number') return st.navId;
  const navId = ++seq;
  session.setItem('ct:nav:seq', String(seq));
  if (!firstId) session.setItem('ct:nav:first', String(navId));
  hist?.replaceState({ navId }, '', location.href);
  return navId;
}
if (hist && 'scrollRestoration' in hist) hist.scrollRestoration = 'manual';
let currentNavId = ensureNavId();
/** Whether the Back button has somewhere to go within this app session. */
export const canGoBack = signal(currentNavId !== (firstId ?? currentNavId));

function restoreScroll(target: number): void {
  // The page may still be loading; keep trying until it is tall enough (or give up after ~1.5 s).
  const started = performance.now();
  const attempt = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max >= target || performance.now() - started > 1500) {
      window.scrollTo(0, Math.min(target, Math.max(0, max)));
      return;
    }
    requestAnimationFrame(attempt);
  };
  requestAnimationFrame(attempt);
}

window.addEventListener('hashchange', () => {
  scrollPositions.set(currentNavId, window.scrollY);
  const existing = (history.state as NavState)?.navId; // set → back/forward to a known entry
  route.value = parseHash();
  currentNavId = ensureNavId();
  canGoBack.value = firstId !== undefined ? currentNavId !== firstId : (hist?.length ?? 1) > 1;
  restoreScroll(existing !== undefined ? (scrollPositions.get(existing) ?? 0) : 0);
});

export type QueryInit = Record<string, string | number | boolean | undefined | null>;

/** Build an href for an anchor: href('/log/abc', { size: 10 }) → '#/log/abc?size=10'. */
export function href(path: string, query?: QueryInit): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
  }
  const q = qs.toString();
  return '#' + path + (q ? '?' + q : '');
}

export function navigate(path: string, query?: QueryInit, replace = false): void {
  const h = href(path, query);
  if (replace) {
    history.replaceState(history.state, '', h); // keep the entry's navId
    route.value = parseHash();
  } else {
    location.hash = h.slice(1);
  }
}

/** The current route as 'path?query' (what `from=` carries). */
export function currentRoutePath(): string {
  const { path, query } = route.value;
  const q = query.toString();
  return path + (q ? '?' + q : '');
}

/** Adds `from=<current route>` to an href so the target page can offer a way back. */
export function withFrom(h: string, from: string = currentRoutePath()): string {
  return h + (h.includes('?') ? '&' : '?') + 'from=' + encodeURIComponent(from);
}

/** Splits a 'path?query' string (e.g. a `from` value) back into a route. */
export function parseRoutePath(s: string): Route {
  const qi = s.indexOf('?');
  const path = qi === -1 ? s : s.slice(0, qi);
  return { path: path.startsWith('/') ? path : '/' + path, query: new URLSearchParams(qi === -1 ? '' : s.slice(qi + 1)) };
}

/** Match '/log/:id/entry/:index' against a path; returns decoded params or null. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const pp = pattern.split('/').filter(Boolean);
  const sp = path.split('/').filter(Boolean);
  if (pp.length !== sp.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    const p = pp[i]!;
    const s = sp[i]!;
    if (p.startsWith(':')) {
      try {
        params[p.slice(1)] = decodeURIComponent(s);
      } catch {
        return null;
      }
    } else if (p !== s) {
      return null;
    }
  }
  return params;
}
